// The JavaScript half of Doc (fire/app/models/doc.rb) — the same two masks
// and the same check digits, for the field somebody is still typing into.
//
// It sits in controllers/ and is not one: that directory is the only place
// on both roads into this engine — an esbuild host aliases @fire at it, an
// importmap host has it on the asset path — so a module every app shares has
// to live here to be reachable from both. Import it as '@fire/doc', pin it
// as 'doc'.

// Everything that is not a digit or a letter, gone, and the rest upcased —
// a CNPJ issued from July 2026 carries letters in its first twelve.
export const digits = (value) =>
  (value ?? "").toString().toUpperCase().replace(/[^0-9A-Z]/g, "");

export const CPF = 11;
export const CNPJ = 14;

// 52998224725 → 529.982.247-25, growing as it is typed.
export const cpfMask = (value) =>
  digits(value)
    .slice(0, CPF)
    .replace(
      /^(\d{3})(\d{0,3})(\d{0,3})(\d{0,2}).*/,
      (_, a, b, c, d) => a + (b && "." + b) + (c && "." + c) + (d && "-" + d),
    );

// 11222333000181 → 11.222.333/0001-81, 12ABC34501DE35 → 12.ABC.345/01DE-35.
export const cnpjMask = (value) =>
  digits(value)
    .slice(0, CNPJ)
    .replace(
      /^([0-9A-Z]{2})([0-9A-Z]{0,3})([0-9A-Z]{0,3})([0-9A-Z]{0,4})(\d{0,2}).*/,
      (_, a, b, c, d, e) =>
        a + (b && "." + b) + (c && "." + c) + (d && "/" + d) + (e && "-" + e),
    );

// Finished values, same as Doc.kind: cpf / cnpj / pass / id.
export const kind = (value) => {
  const d = digits(value);
  if (d.length === CNPJ) return "cnpj";
  if (d.length === CPF && /^\d+$/.test(d)) return "cpf";
  if (/[A-Z]/.test(d)) return "pass";
  if (d.length) return "id";
  return null;
};

// Typing: digits grow as CPF then CNPJ. What a CNPJ can never become is
// not ours to shape, the way Doc.mask leaves it: letters before the twelfth
// character (a passport) stay as typed, and past fourteen (a CNS, somebody
// else's id) the CNPJ's marks come back off. A letter at twelve-plus is the
// alphanumeric CNPJ.
export const mask = (value) => {
  const d = digits(value);
  const letters = /[A-Z]/.test(d);
  if (d.length > CNPJ) return d;
  if (letters && !/^[0-9A-Z]{12}\d{0,2}$/.test(d)) return (value ?? "").toString();
  return d.length > CPF || letters ? cnpjMask(value) : cpfMask(value);
};

// A character is worth its ASCII minus 48 — "0" is 0 and "A" is 17, which is
// how the alphanumeric CNPJ is checked (IN RFB 2.229/2024). All-digit
// documents come out as the digits they are.
const values = (s) => [...s].map((c) => c.charCodeAt(0) - 48);

// Two rules, not one, and this file used to have only the second: a CPF's
// weights run from (size + 1) down to 2, a CNPJ's cycle 2..9 from the right.
// The mirrors of CpfValidator#check_digit and CnpjValidator#check_digit.
const cpfDigit = (ds) => {
  const top = ds.length + 1;
  const mod = ds.reduce((sum, d, i) => sum + d * (top - i), 0) % 11;
  return mod < 2 ? 0 : 11 - mod;
};

const checkDigit = (ds) => {
  let weight = 2;
  let sum = 0;
  for (let i = ds.length - 1; i >= 0; i--) {
    sum += ds[i] * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  const mod = sum % 11;
  return mod < 2 ? 0 : 11 - mod;
};

const checks = (value, length) => {
  const d = digits(value);
  if (d.length !== length || /^(.)\1+$/.test(d)) return false;
  // Letters only in a CNPJ, and never in the two check digits.
  if (length === CPF ? !/^\d+$/.test(d) : !/^[0-9A-Z]{12}\d{2}$/.test(d)) return false;
  const ns = values(d);
  const body = length - 2;
  const dv = length === CPF ? cpfDigit : checkDigit;
  return dv(ns.slice(0, body)) === ns[body] && dv(ns.slice(0, body + 1)) === ns[body + 1];
};

// The deny list CpfValidator carries: arithmetically fine, never a person.
const CPF_DENY = new Set([
  "12345678909",
  "01234567890",
]);

export const validCpf = (value) =>
  !CPF_DENY.has(digits(value)) && checks(value, CPF);
export const validCnpj = (value) => checks(value, CNPJ);

// Whichever it is by length; anything else is neither.
export const valid = (value) => {
  const n = digits(value).length;
  if (n === CPF) return validCpf(value);
  if (n === CNPJ) return validCnpj(value);
  return false;
};

// The access key under a receipt's QR: 44 characters, the emitter's CNPJ at
// 7-20 (letters there since July 2026). The same pattern as CfeValidator::KEY.
export const KEY = /\d{6}[0-9A-Z]{12}\d{26}/;
